import React, { createContext, useState, useContext, useMemo, useEffect } from "react";
import { getCompanies, getCompany, getGeneratedContents } from "../utils/api";

const CompanyContext = createContext();

export const useCompany = () => {
    const context = useContext(CompanyContext);
    if (!context) {
        throw new Error("useCompany must be used within a CompanyProvider");
    }
    return context;
};

export const CompanyProvider = ({ children }) => {
    const [companyId, setCompanyId] = useState(null);
    const [companyKnowledgeDocs, setCompanyKnowledgeDocs] = useState([]);
    const [generatedContents, setGeneratedContents] = useState([]);
    const [loadingKnowledgeBase, setLoadingKnowledgeBase] = useState(false);

    const loadCompanyKnowledgeBase = React.useCallback(async (id = companyId) => {
        if (!id) return;
        
        try {
            setLoadingKnowledgeBase(true);
            
            // Use the getCompany function which handles authentication properly
            const companyData = await getCompany(id);
            
            if (companyData.additional_infos?.knowledge_base) {
                const knowledgeBase = companyData.additional_infos.knowledge_base;
                if (Array.isArray(knowledgeBase)) {
                    setCompanyKnowledgeDocs(knowledgeBase);
                      } else {
                    setCompanyKnowledgeDocs([]);
                }
            } else {
                setCompanyKnowledgeDocs([]);
            }
        } catch (error) {
            console.error('CompanyContext - Failed to load company knowledge base:', error);
            if (error.response) {
                console.error('CompanyContext - Error response:', error.response.status, error.response.data);
            }
        } finally {
            setLoadingKnowledgeBase(false);
        }
    }, [companyId]);

    const loadGeneratedContents = React.useCallback(async () => {
        try {
            const generatedData = await getGeneratedContents();
            
            if (generatedData && typeof generatedData === 'object') {
                // Flatten the object structure into an array of all generated contents
                const allGeneratedContents = [];
                
                Object.keys(generatedData).forEach(contentType => {
                    if (Array.isArray(generatedData[contentType])) {
                        generatedData[contentType].forEach(item => {
                            allGeneratedContents.push({
                                ...item,
                                contentType: contentType,
                                // Use 'name' for Blog, EmailTemplate, Chat and 'title' for Product, FreeFormContent
                                displayName: item.name || item.title || `Generated ${contentType} ${item.id}`,
                                // Create a unique identifier
                                uniqueId: `${contentType}_${item.id}`
                            });
                        });
                    }
                });
                
                setGeneratedContents(allGeneratedContents);
                console.log('CompanyContext - Generated contents loaded:', allGeneratedContents.length, 'items across', Object.keys(generatedData).length, 'content types');
            } else {
                console.warn('CompanyContext - Generated contents is not an object:', typeof generatedData, generatedData);
                setGeneratedContents([]);
            }
        } catch (error) {
            console.error('CompanyContext - Failed to load generated contents:', error);
            if (error.response) {
                console.error('CompanyContext - Error response:', error.response.status, error.response.data);
            }
            setGeneratedContents([]);
        }
    }, []);

    // Initialize company ID and load knowledge base on mount
    useEffect(() => {
        const initializeCompany = async () => {
            try {
                const token = localStorage.getItem('token');
                console.log('CompanyContext - Token check:', token ? 'Token found' : 'No token');
                
                // Skip initialization when there is no token (public access)
                if (!token) {
                    console.log('CompanyContext - Skipping initialization due to missing token (public route)');
                    return;
                }
                
                // Use the getCompanies function which handles authentication properly
                const companies = await getCompanies();
                
                if (companies && companies.length > 0) {
                    const companyId = companies[0].id;
                    setCompanyId(companyId);
                    console.log('CompanyContext - Company ID set:', companyId);
                    // Load knowledge base and generated contents immediately after setting company ID
                    await Promise.all([
                        loadCompanyKnowledgeBase(companyId),
                        loadGeneratedContents()
                    ]);
                } else {
                    console.log('CompanyContext - No companies found');
                    // Load generated contents even when there is no company
                    await loadGeneratedContents();
                }
            } catch (error) {
                console.error('CompanyContext - Failed to initialize company:', error);
                if (error.response) {
                    console.error('CompanyContext - Error response:', error.response.status, error.response.data);
                }
            }
        };
        
        initializeCompany();
    }, [loadCompanyKnowledgeBase, loadGeneratedContents]);

    // Function to force refresh knowledge base (useful after document uploads)
    const refreshKnowledgeBase = React.useCallback(async () => {
        if (companyId) {
            await Promise.all([
                loadCompanyKnowledgeBase(companyId),
                loadGeneratedContents()
            ]);
        }
    }, [companyId, loadCompanyKnowledgeBase, loadGeneratedContents]);

    // Create grouped documents array for the dropdown
    const groupedDocuments = React.useMemo(() => {
        const groups = {
            'Knowledge Base': [],
            'Blog': [],
            'Email Template': [],
            'Product': [],
            'Free Form Content': [],
            'Chat': []
        };

        // Add knowledge base documents
        companyKnowledgeDocs.forEach((doc, index) => {
            const docValue = doc.url || doc.name || doc;
            const docLabel = doc.name || (typeof doc === 'string' ? doc.split("/").pop() : `Knowledge Base Document ${index + 1}`);
            
            groups['Knowledge Base'].push({
                value: docValue,
                label: docLabel,
                source: 'knowledge_base',
                contentType: 'Knowledge Base'
            });
        });

        // Add generated contents grouped by type
        generatedContents.forEach((doc) => {
            const docValue = doc.uniqueId || doc.id || doc;
            const docLabel = doc.displayName || doc.name || doc.title || `Generated ${doc.contentType || 'Content'} ${doc.id}`;
            const groupName = doc.contentType === 'EmailTemplate' ? 'Email Template' : 
                             doc.contentType === 'FreeFormContent' ? 'Free Form Content' : 
                             doc.contentType || 'Other';
            
            if (groups[groupName]) {
                groups[groupName].push({
                    value: docValue,
                    label: docLabel,
                    source: 'generated_contents',
                    contentType: doc.contentType
                });
            }
        });

        // Convert to react-select grouped format
        return Object.keys(groups)
            .filter(groupName => groups[groupName].length > 0)
            .map(groupName => ({
                label: groupName,
                options: groups[groupName]
            }));
    }, [companyKnowledgeDocs, generatedContents]);

    // Create flat array for backward compatibility
    const allDocuments = React.useMemo(() => {
        return groupedDocuments.flatMap(group => group.options);
    }, [groupedDocuments]);

    const value = useMemo(() => ({ 
        companyId, 
        setCompanyId, 
        companyKnowledgeDocs, 
        setCompanyKnowledgeDocs,
        generatedContents,
        setGeneratedContents,
        allDocuments,
        groupedDocuments,
        loadingKnowledgeBase,
        setLoadingKnowledgeBase,
        loadCompanyKnowledgeBase,
        loadGeneratedContents,
        refreshKnowledgeBase
    }), [companyId, companyKnowledgeDocs, generatedContents, allDocuments, groupedDocuments, loadingKnowledgeBase, loadCompanyKnowledgeBase, loadGeneratedContents, refreshKnowledgeBase]);

    return (
        <CompanyContext.Provider value={value}>
            {children}
        </CompanyContext.Provider>
    );
};
